// ═══════════════════════════════════════════════════════════════
// BOUNTIFUL SEA - GAME CONSTANTS
// ═══════════════════════════════════════════════════════════════

import { AIConfig, AIPersonality, GameConfig } from "../../types/bountifulSea";

// ─────────────────────────────────────────────────────────────────
// AI PERSONALITY CONFIGURATIONS
// ─────────────────────────────────────────────────────────────────

export const AI_PERSONALITIES: Record<AIPersonality, AIConfig> = {
  cautious: {
    lieThreshold: 0.1, // 10% chance to lie
    bluffMultiplier: 1.15, // When lying, declare actual * 1.15
    portThreshold: 0.3, // Port early (30% of pot captured)
    callThreshold: 0.75, // Only call if 75%+ suspicious
    riskTolerance: 0.25, // Low risk tolerance
  },
  balanced: {
    lieThreshold: 0.35, // 35% chance to lie
    bluffMultiplier: 1.35, // Moderate bluff
    portThreshold: 0.5, // Port at 50% of pot
    callThreshold: 0.55, // Call at moderate suspicion
    riskTolerance: 0.5, // Medium risk tolerance
  },
  reckless: {
    lieThreshold: 0.6, // 60% chance to lie
    bluffMultiplier: 1.6, // Big bluffs
    portThreshold: 0.75, // Greedy - wants most of pot
    callThreshold: 0.35, // Calls on hunches
    riskTolerance: 0.8, // High risk tolerance
  },
};

// ─────────────────────────────────────────────────────────────────
// DEFAULT GAME SETTINGS
// ─────────────────────────────────────────────────────────────────

export const DEFAULT_CONFIG: GameConfig = {
  playerCount: 2,
  startingDice: 3,
  anteAmount: 10,
  shipwreckRule: "depthsClaimAll",
};

// ─────────────────────────────────────────────────────────────────
// GAME LIMITS
// ─────────────────────────────────────────────────────────────────

export const MIN_PLAYERS = 2;
export const MAX_PLAYERS = 6;

export const MIN_STARTING_DICE = 1;
export const MAX_STARTING_DICE = 4;

export const MIN_ANTE = 1;
export const MAX_ANTE = 100;

// ─────────────────────────────────────────────────────────────────
// DEFAULT PLAYER NAMES
// ─────────────────────────────────────────────────────────────────

export const DEFAULT_PLAYER_NAMES: string[] = [
  "Captain",
  "First Mate",
  "Quartermaster",
  "Bosun",
  "Navigator",
  "Scholar",
];

export const AI_PLAYER_NAMES: string[] = [
  "Phatasma",
  "Desgar",
  "Harvggad",
  "Vertred",
  "Veera",
  "Vareyan",
];

// ─────────────────────────────────────────────────────────────────
// TIMING CONSTANTS (milliseconds)
// ─────────────────────────────────────────────────────────────────

export const DICE_ROLL_ANIMATION_MS = 1500;
export const CALL_WINDOW_TIMEOUT_MS = 10000; // Optional timer for calls
export const AI_DECISION_DELAY_MS = 1500; // Pause before AI acts
export const MESSAGE_DISPLAY_MS = 2000;

// ─────────────────────────────────────────────────────────────────
// FLAVOR TEXT
// ─────────────────────────────────────────────────────────────────

export const VOYAGE_START_MESSAGES = [
  "The tide calls to ye...",
  "Fortune favors the bold!",
  "What treasures await?",
  "The sea whispers of gold...",
  "Cast your lot with fate!",
];

export const MAKE_PORT_MESSAGES = [
  "A wise sailor knows when to dock.",
  "Safe harbor at last!",
  "The tavern awaits!",
  "Fortune secured!",
  "Live to sail another day.",
];

export const SINK_MESSAGES = [
  "The depths claim another...",
  "Down to Davy Jones!",
  "A costly voyage...",
  "The sea takes its due.",
  "Better luck next tide.",
];

export const SCOUNDREL_CALL_MESSAGES = [
  "SCOUNDREL!",
  "Ye lying scallywag!",
  "I smell a rat!",
  "Liar! Cheat!",
  "Show yer hand, bilge rat!",
];

export function getRandomMessage(messages: string[]): string {
  return messages[Math.floor(Math.random() * messages.length)];
}
